import React, { useEffect, useMemo, useState } from 'react';
import { Bubble, Justify, Segment } from 'tea-component';
import classNames from 'classnames';
import { selectValByCondition } from '@home/utils/common';
import { SegmentOption } from 'tea-component/lib/segment/SegmentOption';
import SvgIcon from '@home/components/SvgIcon';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { BaseComponentProps } from '../../types/commom';
import './index.scss';

export type GlobalName = {
  textCn: React.ReactNode;
  textEn: React.ReactNode;
};

export interface IDbbSegmentOption {
  // 选项值
  value: string;
  // 弹出气泡内容
  bubble?: GlobalName;
  // 分组 Key
  groupKey?: string;
  // 选项显示的内容
  text: GlobalName;
  // 是否禁用选项
  disabled?: boolean;
  // 选项显示的 Tooltip 内容
  tooltip?: GlobalName;
  // 组件自定义类名
  className?: string;
  // 组件自定义样式
  style?: React.CSSProperties;
  // tab信息
  info?: GlobalName;
}

export interface IDbbSegment extends BaseComponentProps {
  isCnClass?: boolean;
  // 选项
  options: IDbbSegmentOption[];
  // 默认值
  defaultValue?: string;
  // 受控值
  value?: string;
  // 选项后面的插槽
  afterNode?: React.ReactNode | Function;
  // 根据某些tab隐藏后面的插槽
  hideAfterNodeByTabKeys?: string[];
  onChange?: (value: string) => void;
}

const DbbSegment = (props: IDbbSegment) => {
  const {
    options = [],
    defaultValue,
    value: propsValue,
    style,
    isCnClass = true,
    className,
    children,
    afterNode,
    hideAfterNodeByTabKeys = [],
    onChange,
    ...resetProps
  } = props;

  const { isEN } = useLocal();

  // 当前激活tab值
  const [value, setValue] = useState(defaultValue || options?.[0]?.value);

  // 受控值
  useEffect(() => {
    if (propsValue) {
      setValue(propsValue);
    }
  }, [propsValue]);

  // 转换中英文取值
  const getGlobalText = (names?: GlobalName) => {
    const { textCn, textEn } = names || {};
    let text;
    if (typeof textCn === 'string' && typeof textEn === 'string') {
      text = selectValByCondition(isEN, textEn, textCn);
    } else {
      text = isEN ? textEn : textCn;
    }
    return text;
  };

  const { newOptions, activeTab } = useMemo(() => {
    const opts = options?.map((item) => {
      const { bubble, text, tooltip, info, ...otherOpts } = item;
      const opt: SegmentOption = { ...otherOpts };
      // 气泡国际化
      if (bubble) {
        opt.bubble = getGlobalText(bubble);
      }
      // 浮窗国际化
      if (tooltip) {
        opt.tooltip = getGlobalText(tooltip);
      }
      opt.text = getGlobalText(text);
      // tab侧边的info图标信息
      if (info) {
        opt.text = (
          <div className="dbb-segment-info">
            {opt.text}
            <Bubble
              trigger="click"
              arrowPointAtCenter
              placement="top-start"
              content={getGlobalText(info)}
              dark
            >
              <SvgIcon
                type={value === opt.value ? 'tab-tip-icon' : 'alert-circle-m'}
              />
            </Bubble>
          </div>
        );
      }
      return opt;
    });

    const activeTab = options?.find((item) => item.value === value);

    return {
      newOptions: opts,
      activeTab
    };
  }, [options, isEN, value]);

  let newAfterNode = typeof (afterNode) === "function" ? afterNode(value) : afterNode;

  // 隐藏插槽
  if (
    hideAfterNodeByTabKeys?.length > 0 &&
    hideAfterNodeByTabKeys?.includes(value)
  ) {
    newAfterNode = null;
  }

  return (
    <div
      className={classNames('dbb-segment-container', className)}
      style={style}
    >
      <Justify
        left={
          <>
            <Segment
              className={classNames('segment-light', {
                'segment-light-china': !isEN && isCnClass,
              })}
              value={value}
              onChange={(val) => {
                // 解决tea组件bug，重复点击还会触发change事件
                if (value === val) {
                  return;
                }
                onChange?.(val);
                setValue(val);
              }}
              options={newOptions}
            />
            {newAfterNode}
          </>
        }
      />
      <div key={value}>
        {React.Children.map(children, (child: any) =>
          React.cloneElement(child, {
            ...resetProps,
            activeTab,
          }),
        )}
      </div>
    </div>
  );
};

export default DbbSegment;
